import { useState, useEffect, useMemo } from "react";
import {
  Paper,
  Typography,
  Box,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Skeleton,
  Fade,
  Slide,
  Grow,
  LinearProgress,
  TextField,
} from "@mui/material";
import axios from "axios";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DeleteIcon from "@mui/icons-material/Delete";
import { styled, keyframes } from "@mui/system";
import { alpha } from "@mui/material/styles";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DownloadIcon from "@mui/icons-material/Download";
import AddTicketDialog from "./AddTicketDialog";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import BusinessIcon from "@mui/icons-material/Business";
import ApartmentIcon from "@mui/icons-material/Apartment";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import EventIcon from "@mui/icons-material/Event";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import PersonIcon from "@mui/icons-material/Person";
import TitleIcon from "@mui/icons-material/Title";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DateRangeIcon from "@mui/icons-material/DateRange";
import SearchIcon from "@mui/icons-material/Search";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.02); }
  100% { transform: scale(1); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const AnimatedCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "priority" && prop !== "isDragging",
})(({ priority, isDragging }) => ({
  animation: `${fadeIn} 0.4s ease-out`,
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  margin: "12px 0",
  willChange: "transform",
  background: "linear-gradient(135deg, #232526 0%, #2d3134 100%)",
  color: "white",
  borderRight: `8px solid ${priorityColors[priority] || "#333"}`,
  transform: isDragging ? "scale(1.02) rotate(-1deg)" : "scale(1)",
  boxShadow: isDragging
    ? "0 8px 24px rgba(111,217,67,0.35)"
    : "0 4px 12px rgba(0,0,0,0.25)",
  opacity: isDragging ? 0.9 : 1,
  "&:hover": {
    transform: "scale(1.08)",
    zIndex: 100,
    boxShadow: "0 16px 32px rgba(111,217,67,0.25), 0 8px 16px rgba(0,0,0,0.18)",
  },
}));

const columnHeaderColors = {
  البداية: "#1976d2",
  "قيد التنفيذ": "#ed6c02",
  منتهية: "#2e7d32",
  مؤرشف: "#757575",
};

const ColumnHeader = styled(Box)(({ col }) => ({
  padding: "18px 0 12px 0",
  background: `linear-gradient(135deg, ${
    columnHeaderColors[col] || "#333"
  } 0%, #232526 100%)`,
  borderRadius: "14px 14px 0 0",
  boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
  marginBottom: "8px",
  textAlign: "center",
  position: "relative",
}));

const ColumnContainer = styled(Box)({
  background: "#25282b",
  borderRadius: "8px",
  boxShadow: "0 4px 20px rgba(0,0,0,0.25)",
  overflowY: "auto",
  padding: "8px",
  "&::-webkit-scrollbar": {
    width: "6px",
  },
  "&::-webkit-scrollbar-track": {
    background: "#1a1d20",
  },
  "&::-webkit-scrollbar-thumb": {
    background: "#6fd943",
    borderRadius: "3px",
  },
});

const priorityColors = {
  Low: "#2e7d32",
  Normal: "#1976d2",
  High: "#ed6c02",
  Critical: "#d32f2f",
};

const priorityLabels = {
  Low: "منخفضة",
  Normal: "عادية",
  High: "مرتفعة",
  Critical: "حرجة",
};

const priorityBadgeStyles = {
  Low: { background: "#388e3c", color: "#fff" },
  Normal: { background: "#1976d2", color: "#fff" },
  High: { background: "#ed6c02", color: "#fff" },
  Critical: { background: "#d32f2f", color: "#fff" },
};

const playNotification = () => {
  const audio = new Audio("/sounds/notification_ticket.wav");
  audio.play();
};

const TicketsForAdmin = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]); // <-- Add departments state
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState(null);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openAnswerDialog, setOpenAnswerDialog] = useState(false);
  const [answerText, setAnswerText] = useState("");
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const companies = useMemo(
    () => ["SEG", "ElBatt", "ZBooma", "Sapaaq", "Moharek", "Daad"],
    []
  );
  const columns = useMemo(
    () => ["البداية", "قيد التنفيذ", "منتهية", "مؤرشف"],
    []
  );
  const user = useMemo(() => localStorage.getItem("data"), []);
  const [attachment, setAttachment] = useState(null);
  const [filter, setFilter] = useState({
    department: "",
    company: "",
    priority: "",
    status: "",
    title: "",
    assigned_to: "",
    username: "",
    hasAttachment: "",
    ticketType: "", // فلتر جديد
    duration: "", // فلتر مدة التذكرة
  });
  const [dateRange, setDateRange] = useState({
    fromDate: "",
    toDate: "",
  });
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const months = useMemo(
    () =>
      Array.from(
        new Set(
          tickets
            .map((t) => t.created_at && t.created_at.slice(0, 7)) // "YYYY-MM"
            .filter(Boolean)
        )
      ).sort(),
    [tickets]
  );

  const uniqueUsernames = useMemo(
    () => Array.from(new Set(tickets.map((t) => t.username))).filter(Boolean),
    [tickets]
  );
  const currentMonth = useMemo(() => new Date().toISOString().slice(0, 7), []);

  const [selectedMonth, setSelectedMonth] = useState(currentMonth);

  const clearAllFilters = () => {
    setFilter({
      department: "",
      company: "",
      priority: "",
      status: "",
      assigned_to: "",
      username: "",
      hasAttachment: "",
      ticketType: "", // مسح الفلتر الجديد
      duration: "", // مسح فلتر المدة
    });
    setSelectedMonth("");
    setDateRange({
      fromDate: "",
      toDate: "",
    });
    setSearchText("");
    setDebouncedSearch("");
  };

  const hasActiveFilters = useMemo(
    () =>
      filter.department ||
      filter.company ||
      filter.priority ||
      filter.status ||
      filter.assigned_to ||
      filter.username ||
      filter.hasAttachment ||
      filter.ticketType || // إضافة الفلتر الجديد
      filter.duration || // إضافة فلتر المدة
      selectedMonth ||
      dateRange.fromDate ||
      dateRange.toDate ||
      debouncedSearch,
    [filter, selectedMonth, dateRange, debouncedSearch]
  );

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const response = await axios.get(
          "https://backenderpforseg.samielmetwali.com/tickets/getAllTicketsForAllUsers.php"
        );
        if (response.data.success) {
          const data = response.data.data.map((t) => ({
            ...t,
            priority:
              t.priority === "Low" ||
              t.priority === "Normal" ||
              t.priority === "High" ||
              t.priority === "Critical"
                ? t.priority
                : "Normal",
            assigned_to: t.assigned_to || "غير مخصص",
          }));
          setTickets(data);
        } else {
          setTickets([]);
        }
      } catch (error) {
        console.error("Error fetching tickets:", error);
        setTickets([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchEmployees = async () => {
      try {
        const response = await axios.get(
          "https://backenderpforseg.samielmetwali.com/getemployees.php"
        );
        if (response.data.status === "success") {
          const itEmployees = response.data.data.filter(
            (emp) =>
              emp.department2 &&
              typeof emp.department2 === "string" &&
              emp.department2.trim().toLowerCase() === "it" &&
              emp.role &&
              typeof emp.role === "string" &&
              emp.role.trim().toLowerCase() !== "head"
          );
          setEmployees(itEmployees);
        }
      } catch (error) {
        console.error("Error fetching employees:", error);
      }
    };

    const fetchDepartments = async () => {
      try {
        const response = await axios.get("https://backenderpforseg.samielmetwali.com/getDepartments.php");
        if (response.data.success) {
          setDepartments(response.data.departments);
        } else {
          setDepartments([]);
        }
      } catch (error) {
        setDepartments([]);
      }
    };

    fetchTickets();
    fetchEmployees();
    fetchDepartments(); // <-- Fetch departments
  }, []);

  useEffect(() => {
    if (searchText === "") {
      setDebouncedSearch("");
      return;
    }

    const handler = setTimeout(() => {
      setDebouncedSearch(searchText);
    }, 300); // تقليل الوقت لـ 300ms

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  useEffect(() => {
    if (tickets.length > 0) {
      setSelectedMonth(currentMonth);
    }
  }, [tickets]);

  const handleAssign = (ticketId, employeeId) => {
    setTickets((prevTickets) =>
      prevTickets.map((ticket) =>
        ticket.id === ticketId ? { ...ticket, assigned_to: employeeId } : ticket
      )
    );
    playNotification();
    updateTicketBackend(ticketId, { assigned_to: employeeId });
  };

  const handlePriorityChange = (ticketId, newPriority) => {
    setTickets((prevTickets) =>
      prevTickets.map((ticket) =>
        ticket.id === ticketId ? { ...ticket, priority: newPriority } : ticket
      )
    );
    updateTicketBackend(ticketId, { priority: newPriority });
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;
    const draggedTicket = tickets.find(
      (t) => `${t.status}-${t.id}` === result.draggableId
    );
    if (draggedTicket) {
      // إذا لم يتغير العمود فقط أعد الترتيب العادي
      if (source.droppableId === destination.droppableId) {
        return;
      }
      // غيّر الحالة وأضف التذكرة في نهاية العمود الجديد
      const updatedTickets = tickets
        .filter((ticket) => ticket.id !== draggedTicket.id)
        .concat([{ ...draggedTicket, status: destination.droppableId }]);
      setTickets(updatedTickets);
      playNotification();
      updateTicketBackend(draggedTicket.id, {
        status: destination.droppableId,
      });
    }
  };

  const confirmDelete = (ticketId) => {
    setTicketToDelete(ticketId);
    setOpenDeleteDialog(true);
  };

  const updateTicketBackend = async (id, updates) => {
    try {
      await axios.post(
        "https://backenderpforseg.samielmetwali.com/tickets/updateTicket.php",
        {
          id,
          ...updates,
        }
      );
    } catch (error) {
      console.error("خطأ في تحديث التذكرة:", error);
    }
  };

  const deleteConfirmed = async () => {
    try {
      const response = await axios.post(
        "https://backenderpforseg.samielmetwali.com/tickets/deleteTicket.php",
        { id: ticketToDelete }
      );
      console.log("DELETE RESPONSE:", response.data);

      if (response.data.success) {
        setTickets((prev) => prev.filter((t) => t.id !== ticketToDelete));
        setOpenDeleteDialog(false);
        setTicketToDelete(null);
        setDeleteSuccess(true);
        playNotification();
      } else {
        alert("فشل في حذف التذكرة: " + response.data.message);
      }
    } catch (error) {
      console.error("Error deleting ticket:", error);
    }
  };

  // ✅ First define filteredTickets
  const filteredTickets = useMemo(
    () =>
      tickets.filter(
        (ticket) =>
          (!filter.department || ticket.department === filter.department) &&
          (!filter.company || ticket.company === filter.company) &&
          (!filter.priority || ticket.priority === filter.priority) &&
          (!filter.status || ticket.status === filter.status) &&
          (!filter.assigned_to || ticket.assigned_to === filter.assigned_to) &&
          (!filter.username || ticket.username === filter.username) &&
          (!filter.hasAttachment ||
            (filter.hasAttachment === "yes" && ticket.attachment) ||
            (filter.hasAttachment === "no" && !ticket.attachment)) &&
          (!filter.ticketType || ticket.ticketType === filter.ticketType) && // إضافة منطق الفلتر الجديد
          (!selectedMonth ||
            (ticket.created_at &&
              ticket.created_at.slice(0, 7) === selectedMonth)) &&
          (!dateRange.fromDate ||
            (ticket.created_at && ticket.created_at >= dateRange.fromDate)) &&
          (!dateRange.toDate ||
            (ticket.created_at &&
              ticket.created_at <= dateRange.toDate + " 23:59:59")) &&
          (!debouncedSearch ||
            (ticket.title &&
              ticket.title
                .toLowerCase()
                .includes(debouncedSearch.toLowerCase())) ||
            (ticket.description &&
              ticket.description &&
              ticket.description
                .toLowerCase()
                .includes(debouncedSearch.toLowerCase())) ||
            (ticket.username &&
              ticket.username
                .toLowerCase()
                .includes(debouncedSearch.toLowerCase())))
      ),
    [tickets, filter, selectedMonth, dateRange, debouncedSearch]
  );

  // ✅ Then calculate statistics using the defined filteredTickets
  const statistics = useMemo(() => {
    const total = filteredTickets.length;
    const lowPriority = filteredTickets.filter(
      (t) => t.priority === "Low"
    ).length;
    const normalPriority = filteredTickets.filter(
      (t) => t.priority === "Normal"
    ).length;
    const highPriority = filteredTickets.filter(
      (t) => t.priority === "High"
    ).length;
    const criticalPriority = filteredTickets.filter(
      (t) => t.priority === "Critical"
    ).length;

    return {
      total,
      lowPriority,
      normalPriority,
      highPriority,
      criticalPriority,
    };
  }, [filteredTickets]);

  const { total, lowPriority, normalPriority, highPriority, criticalPriority } =
    statistics;

  // Memoize column counts
  const columnCounts = useMemo(() => {
    const counts = {};
    columns.forEach((col) => {
      counts[col] = filteredTickets.filter((t) => t.status === col).length;
    });
    return counts;
  }, [filteredTickets, columns]);

  const FilterBar = useMemo(
    () =>
      styled(Box)({
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(135deg, #232526 0%, #2d3134 100%)",
        borderRadius: 16,
        padding: "20px 24px",
        marginBottom: 24,
        boxShadow:
          "0 4px 20px rgba(111,217,67,0.12), 0 2px 8px rgba(0,0,0,0.3)",
        border: "1px solid rgba(111,217,67,0.15)",
        position: "relative",
        transition: "all 0.3s ease",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "2px",
          background: "linear-gradient(90deg, #6fd943, #4caf50, #6fd943)",
          borderRadius: "16px 16px 0 0",
        },
      }),
    []
  );

  const FilterHeader = useMemo(
    () =>
      styled(Box)({
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        cursor: "pointer",
        padding: "8px 0",
        "&:hover": {
          "& .filter-toggle-icon": {
            transform: "scale(1.1)",
          },
        },
      }),
    []
  );

  const FilterPanel = useMemo(
    () =>
      styled(Box)(({ open }) => ({
        display: "flex",
        flexDirection: "column",
        gap: 16,
        overflow: "hidden",
        transition: "all 0.3s ease",
        maxHeight: open ? "1000px" : "0",
        opacity: open ? 1 : 0,
        paddingTop: open ? "16px" : "0",
        borderTop: open ? "1px solid rgba(111,217,67,0.2)" : "none",
      })),
    []
  );

  const FilterRow = useMemo(
    () =>
      styled(Box)({
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        "@media (max-width: 768px)": {
          flexDirection: "column",
          alignItems: "stretch",
          gap: 8,
        },
      }),
    []
  );

  const FilterGroup = useMemo(
    () =>
      styled(Box)({
        display: "flex",
        flexDirection: "column",
        gap: 12,
        padding: "16px",
        background: "rgba(35, 37, 38, 0.3)",
        borderRadius: 12,
        border: "1px solid rgba(111,217,67,0.1)",
      }),
    []
  );

  const FilterGroupTitle = useMemo(
    () =>
      styled(Typography)({
        color: "#6fd943",
        fontWeight: 700,
        fontSize: "14px",
        marginBottom: "8px",
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }),
    []
  );

  const StyledSelect = styled(Select)({
    minWidth: 150,
    background: "rgba(35, 37, 38, 0.8)",
    color: "#fff",
    borderRadius: 10,
    fontWeight: 600,
    transition: "all 0.3s ease",
    "& .MuiSelect-icon": {
      color: "#6fd943",
      transition: "transform 0.2s ease",
    },
    "&:hover": {
      background: "rgba(38, 42, 45, 0.9)",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(111,217,67,0.2)",
      "& .MuiSelect-icon": {
        transform: "rotate(180deg)",
      },
    },
    "&.Mui-focused": {
      background: "rgba(38, 42, 45, 1)",
      borderColor: "#6fd943",
      boxShadow: "0 0 0 2px rgba(111,217,67,0.2)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(111,217,67,0.3)",
      borderWidth: "1.5px",
    },
    "& .MuiSelect-select": {
      padding: "8px 12px",
      fontSize: "14px",
    },
    marginRight: 8,
    "@media (max-width: 768px)": {
      minWidth: "100%",
      marginRight: 0,
      marginBottom: 8,
    },
  });

  const TicketCounter = styled(Box)({
    display: "flex",
    alignItems: "center",
    gap: 8,
    background: "linear-gradient(135deg, #6fd943 0%, #4caf50 100%)",
    color: "#fff",
    padding: "8px 16px",
    borderRadius: 20,
    fontWeight: 700,
    fontSize: "14px",
    boxShadow: "0 2px 8px rgba(111,217,67,0.3)",
    border: "1px solid rgba(255,255,255,0.2)",
    minWidth: "fit-content",
    "& .counter-number": {
      background: "rgba(255,255,255,0.2)",
      borderRadius: "50%",
      width: 24,
      height: 24,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: "12px",
      fontWeight: 900,
    },
  });

  const FilterLabel = styled(Typography)({
    color: "#6fd943",
    fontWeight: 700,
    fontSize: "16px",
    marginRight: 8,
    textShadow: "0 0 8px rgba(111,217,67,0.3)",
  });

  return (
    <Fade in={true} timeout={800}>
      <Paper
        style={{
          padding: "24px",
          marginBottom: "16px",
          fontFamily: "Cairo, sans-serif",
          direction: "rtl",
          backgroundColor: "#1a1d20",
          color: "white",
          overflowX: "auto",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          style={{
            fontFamily: "Cairo, sans-serif",
            color: "#6fd943",
            fontWeight: 700,
            marginBottom: "24px",
            textAlign: "center",
            textShadow: "0 0 10px rgba(111, 217, 67, 0.4)",
          }}
        >
          لوحة إدارة التذاكر
        </Typography>
        {/* Ticket Statistics for role=head */}
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {/* Priority Cards */}
          {[
            {
              label: "عدد التذاكر",
              value: total,
              color: "#fff",
              icon: (
                <ConfirmationNumberIcon
                  sx={{ color: "#6fd943", fontSize: 20 }}
                />
              ),
            },
            {
              label: "منخفضة",
              value: lowPriority,
              color: "#388e3c",
              icon: (
                <ArrowDownwardIcon sx={{ color: "#388e3c", fontSize: 20 }} />
              ),
            },
            {
              label: "عادية",
              value: normalPriority,
              color: "#1976d2",
              icon: (
                <HorizontalRuleIcon sx={{ color: "#1976d2", fontSize: 20 }} />
              ),
            },
            {
              label: "مرتفعة",
              value: highPriority,
              color: "#ed6c02",
              icon: <ArrowUpwardIcon sx={{ color: "#ed6c02", fontSize: 20 }} />,
            },
            {
              label: "حرجة",
              value: criticalPriority,
              color: "#d32f2f",
              icon: (
                <WarningAmberIcon sx={{ color: "#d32f2f", fontSize: 20 }} />
              ),
            },
          ].map((priority, index) => (
            <Grid item xs={12} md={2.4} key={index}>
              <Card
                sx={{
                  background:
                    "linear-gradient(to bottom right, #2d2d2d, #1f1f1f)",
                  color: "#fff",
                  borderRadius: "8px",
                  boxShadow: 3,
                  borderTop: `4px solid ${priority.color}`,
                  transition: "transform 0.3s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle2"
                        sx={{ opacity: 0.8, mb: 0.5 }}
                      >
                        {priority.label}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700 }}>
                        {priorityLabels[priority.label] || priority.value}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        bgcolor: alpha(priority.color, 0.2),
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {priority.icon}
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={total > 0 ? (priority.value / total) * 100 : 0}
                    sx={{
                      mt: 2,
                      height: 6,
                      borderRadius: 3,
                      bgcolor: alpha(priority.color, 0.2),
                      "& .MuiLinearProgress-bar": {
                        bgcolor: priority.color,
                      },
                    }}
                  />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
        <Button
          variant="contained"
          color="success"
          sx={{ mb: 2, fontWeight: 600 }}
          onClick={() => setOpenAddDialog(true)}
        >
          إضافة تذكرة جديدة
        </Button>
        {loading ? (
          <Box style={{ minWidth: "900px" }}>
            <Grid container spacing={3}>
              {columns.map((col) => (
                <Grid item xs={12} md={4} key={col}>
                  <Skeleton
                    variant="rounded"
                    width="100%"
                    height={40}
                    sx={{ bgcolor: "#2a2e31" }}
                  />
                  <Box mt={2}>
                    {[1, 2, 3].map((item) => (
                      <Skeleton
                        key={item}
                        variant="rounded"
                        width="100%"
                        height={180}
                        sx={{
                          bgcolor: "#2a2e31",
                          mb: 2,
                          borderRadius: "8px",
                        }}
                      />
                    ))}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Box style={{ minWidth: "900px" }}>
            <FilterBar>
              <FilterHeader
                onClick={() => setFilterPanelOpen(!filterPanelOpen)}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <FilterAltIcon sx={{ color: "#6fd943", fontSize: 28 }} />
                  <FilterLabel>تصفية التذاكر</FilterLabel>
                  {hasActiveFilters && (
                    <Box
                      sx={{
                        background: "#6fd943",
                        color: "#fff",
                        borderRadius: "50%",
                        width: 20,
                        height: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "12px",
                        fontWeight: "bold",
                      }}
                    >
                      {Object.values(filter).filter(Boolean).length +
                        (selectedMonth ? 1 : 0) +
                        (dateRange.fromDate || dateRange.toDate ? 1 : 0) +
                        (searchText ? 1 : 0)}
                    </Box>
                  )}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <TicketCounter>
                    <ConfirmationNumberIcon sx={{ fontSize: 16 }} />
                    <span>التذاكر المطابقة:</span>
                    <span className="counter-number">
                      {filteredTickets.length}
                    </span>
                  </TicketCounter>
                  {filterPanelOpen ? (
                    <ExpandLessIcon
                      className="filter-toggle-icon"
                      sx={{ color: "#6fd943", fontSize: 24 }}
                    />
                  ) : (
                    <ExpandMoreIcon
                      className="filter-toggle-icon"
                      sx={{ color: "#6fd943", fontSize: 24 }}
                    />
                  )}
                </Box>
              </FilterHeader>

              <FilterPanel open={filterPanelOpen}>
                {/* Basic Filters Group */}
                <FilterGroup>
                  <FilterGroupTitle>
                    <ApartmentIcon /> الفلاتر الأساسية
                  </FilterGroupTitle>
                  <FilterRow>
                    <StyledSelect
                      value={filter.department}
                      onChange={(e) =>
                        setFilter((f) => ({
                          ...f,
                          department: e.target.value,
                        }))
                      }
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <BusinessIcon sx={{ color: "#6fd943", mr: 1 }} /> كل الأقسام
                      </MenuItem>
                      {departments.map((dep) => (
                        <MenuItem key={dep.id} value={dep.name}>
                          {dep.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>

                    <StyledSelect
                      value={filter.company}
                      onChange={(e) =>
                        setFilter((f) => ({ ...f, company: e.target.value }))
                      }
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <BusinessIcon sx={{ color: "#6fd943", mr: 1 }} /> كل
                        الشركات
                      </MenuItem>
                      <MenuItem value="SEG">SEG</MenuItem>
                      <MenuItem value="ElBatt">ElBatt</MenuItem>
                      <MenuItem value="ZBooma">ZBooma</MenuItem>
                      <MenuItem value="Sapaaq">Sapaaq</MenuItem>
                      <MenuItem value="Moharek">Moharek</MenuItem>
                      <MenuItem value="Daad">Daad</MenuItem>
                    </StyledSelect>

                    <StyledSelect
                      value={filter.priority}
                      onChange={(e) =>
                        setFilter((f) => ({ ...f, priority: e.target.value }))}
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <PriorityHighIcon sx={{ color: "#6fd943", mr: 1 }} /> كل الأولويات
                      </MenuItem>
                      <MenuItem value="Low">منخفضة</MenuItem>
                      <MenuItem value="Normal">عادية</MenuItem>
                      <MenuItem value="High">مرتفعة</MenuItem>
                      <MenuItem value="Critical">حرجة</MenuItem>
                    </StyledSelect>

                    <StyledSelect
                      value={filter.status}
                      onChange={(e) =>
                        setFilter((f) => ({ ...f, status: e.target.value }))
                      }
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <EventIcon sx={{ color: "#6fd943", mr: 1 }} /> كل
                        الحالات
                      </MenuItem>
                      <MenuItem value="البداية">البداية</MenuItem>
                      <MenuItem value="قيد التنفيذ">قيد التنفيذ</MenuItem>
                      <MenuItem value="منتهية">منتهية</MenuItem>
                      <MenuItem value="مؤرشف">مؤرشف</MenuItem>
                    </StyledSelect>
                  </FilterRow>
                </FilterGroup>

                {/* Advanced Filters Group */}
                <FilterGroup>
                  <FilterGroupTitle>
                    <SearchIcon /> الفلاتر المتقدمة
                  </FilterGroupTitle>
                  <FilterRow>
                    <StyledSelect
                      value={filter.assigned_to}
                      onChange={(e) =>
                        setFilter((f) => ({
                          ...f,
                          assigned_to: e.target.value,
                        }))
                      }
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <PersonIcon sx={{ color: "#6fd943", mr: 1 }} /> كل
                        الموظفين
                      </MenuItem>
                      {employees.map((emp) => (
                        <MenuItem key={emp.employee_id} value={emp.employee_id}>
                          {emp.name}
                        </MenuItem>
                      ))}
                    </StyledSelect>

                    <StyledSelect
                      value={filter.hasAttachment}
                      onChange={(e) =>
                        setFilter((f) => ({
                          ...f,
                          hasAttachment: e.target.value,
                        }))
                      }
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <AttachFileIcon sx={{ color: "#6fd943", mr: 1 }} /> كل
                        المرفقات
                      </MenuItem>
                      <MenuItem value="yes">مع مرفقات</MenuItem>
                      <MenuItem value="no">بدون مرفقات</MenuItem>
                    </StyledSelect>

                    <StyledSelect
                      value={filter.ticketType}
                      onChange={(e) =>
                        setFilter((f) => ({
                          ...f,
                          ticketType: e.target.value,
                        }))
                      }
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <TitleIcon sx={{ color: "#6fd943", mr: 1 }} /> كل
                        الأنواع
                      </MenuItem>
                      <MenuItem value="bug">مشكلة تقنية</MenuItem>
                      <MenuItem value="feature">طلب ميزة جديدة</MenuItem>
                      <MenuItem value="support">طلب دعم</MenuItem>
                      <MenuItem value="maintenance">صيانة</MenuItem>
                      <MenuItem value="other">أخرى</MenuItem>
                    </StyledSelect>

                    <StyledSelect
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <EventIcon sx={{ color: "#6fd943", mr: 1 }} /> كل الشهور
                      </MenuItem>
                      {months.map((month) => (
                        <MenuItem key={month} value={month}>
                          {month}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FilterRow>
                </FilterGroup>

                {/* Search and Date Filters Group */}
                <FilterGroup>
                  <FilterGroupTitle>
                    <SearchIcon /> البحث والتواريخ
                  </FilterGroupTitle>
                  <FilterRow>
                    <TextField
                      placeholder="البحث في العنوان أو الوصف أو الاسم..."
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      size="small"
                      sx={{
                        flex: 1,
                        "& .MuiInputBase-root": {
                          background: "rgba(35, 37, 38, 0.8)",
                          color: "#fff",
                          borderRadius: 2,
                          "&:hover": {
                            background: "rgba(38, 42, 45, 0.9)",
                          },
                          "&.Mui-focused": {
                            background: "rgba(38, 42, 45, 1)",
                          },
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          borderColor: "rgba(111,217,67,0.3)",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6fd943",
                        },
                        "@media (max-width: 768px)": {
                          minWidth: "100%",
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <SearchIcon sx={{ color: "#6fd943", mr: 1 }} />
                        ),
                      }}
                    />

                    <StyledSelect
                      value={filter.username}
                      onChange={(e) =>
                        setFilter((f) => ({ ...f, username: e.target.value }))
                      }
                      displayEmpty
                      size="small"
                    >
                      <MenuItem value="">
                        <AccountCircleIcon sx={{ color: "#6fd943", mr: 1 }} />{" "}
                        كل المستخدمين
                      </MenuItem>
                      {uniqueUsernames.map((username) => (
                        <MenuItem key={username} value={username}>
                          {username}
                        </MenuItem>
                      ))}
                    </StyledSelect>
                  </FilterRow>

                  <FilterRow>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "center",
                        flex: 1,
                      }}
                    >
                      <DateRangeIcon sx={{ color: "#6fd943" }} />
                      <TextField
                        type="date"
                        value={dateRange.fromDate}
                        onChange={(e) =>
                          setDateRange((prev) => ({
                            ...prev,
                            fromDate: e.target.value,
                          }))
                        }
                        size="small"
                        sx={{
                          flex: 1,
                          "& .MuiInputBase-root": {
                            background: "rgba(35, 37, 38, 0.8)",
                            color: "#fff",
                            borderRadius: 2,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(111,217,67,0.3)",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          sx: { color: "#6fd943" },
                        }}
                        label="من تاريخ"
                      />
                      <Typography sx={{ color: "#6fd943" }}>إلى</Typography>
                      <TextField
                        type="date"
                        value={dateRange.toDate}
                        onChange={(e) =>
                          setDateRange((prev) => ({
                            ...prev,
                            toDate: e.target.value,
                          }))
                        }
                        size="small"
                        sx={{
                          flex: 1,
                          "& .MuiInputBase-root": {
                            background: "rgba(35, 37, 38, 0.8)",
                            color: "#fff",
                            borderRadius: 2,
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "rgba(111,217,67,0.3)",
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                          sx: { color: "#6fd943" },
                        }}
                        label="إلى تاريخ"
                      />
                    </Box>
                  </FilterRow>
                </FilterGroup>

                {/* Clear Filters Button */}
                {hasActiveFilters && (
                  <Box
                    sx={{ display: "flex", justifyContent: "center", pt: 2 }}
                  >
                    <Button
                      variant="outlined"
                      onClick={clearAllFilters}
                      sx={{
                        color: "#6fd943",
                        borderColor: "#6fd943",
                        borderRadius: "12px",
                        fontWeight: 600,
                        px: 4,
                        py: 1,
                        "&:hover": {
                          backgroundColor: "rgba(111,217,67,0.1)",
                          borderColor: "#4caf50",
                          transform: "translateY(-1px)",
                        },
                      }}
                    >
                      مسح جميع الفلاتر
                    </Button>
                  </Box>
                )}
              </FilterPanel>
            </FilterBar>
            <DragDropContext onDragEnd={onDragEnd}>
              <Grid
                container
                spacing={3}
                alignItems="stretch"
                style={{ minHeight: "80vh", paddingBottom: 32 }}
              >
                {columns.map((col) => (
                  <Grid
                    item
                    xs={12}
                    md={3}
                    key={col}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    <ColumnHeader col={col}>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        gap={1}
                      >
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: "50%",
                            background: columnHeaderColors[col] || "#333",
                            boxShadow: `0 0 8px ${
                              columnHeaderColors[col] || "#333"
                            }`,
                          }}
                        />
                        <Typography
                          variant="h6"
                          sx={{
                            color: "#fff",
                            fontWeight: 900,
                            fontSize: 22,
                            letterSpacing: 1,
                            textShadow: "0 2px 8px rgba(0,0,0,0.18)",
                          }}
                        >
                          {col}
                        </Typography>
                        <Box
                          sx={{
                            ml: 1,
                            backgroundColor: "rgba(255,255,255,0.10)",
                            color: "#fff",
                            borderRadius: "12px",
                            px: 1.5,
                            py: 0.2,
                            fontSize: "1rem",
                            fontWeight: 700,
                          }}
                        >
                          {columnCounts[col]}
                        </Box>
                      </Box>
                    </ColumnHeader>
                    <Droppable droppableId={col}>
                      {(provided) => (
                        <ColumnContainer
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "visible", // مهم جداً
                            padding: "8px",
                          }}
                        >
                          {filteredTickets
                            .filter((t) => t.status === col)
                            .map((ticket, index) => (
                              <Draggable
                                key={`${ticket.status}-${ticket.id}`}
                                draggableId={`${ticket.status}-${ticket.id}`}
                                index={index}
                                isDragDisabled={
                                  !ticket.assigned_to || !ticket.status
                                }
                              >
                                {(provided, snapshot) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    style={{
                                      ...provided.draggableProps.style,
                                      marginBottom: "12px",
                                    }}
                                  >
                                    <Grow in={true} timeout={index * 100 + 300}>
                                      <div>
                                        <AnimatedCard
                                          priority={ticket.priority}
                                          isDragging={snapshot.isDragging}
                                        >
                                          <CardContent
                                            sx={{
                                              p: 1.5,
                                              "&:last-child": { pb: 1.5 },
                                              position: 'relative',
                                            }}
                                          >
                                            {/* Ticket ID badge */}
                                            <Box
                                              sx={{
                                                position: 'absolute',
                                                top: 15,
                                                left: 80,
                                                background: '#232a34',
                                                border: '2px solid #6fd943',
                                                color: '#6fd943',
                                                borderRadius: '8px',
                                                px: 1,
                                                py: 0.1,
                                                fontWeight: 'bold',
                                                fontSize: 12,
                                                zIndex: 2,
                                                boxShadow: '0 2px 8px rgba(111,217,67,0.10)',
                                                fontFamily: 'Cairo, sans-serif',
                                                marginBottom: '8px',
                                              }}
                                            >
                                              #{ticket.id}
                                            </Box>
                                            <Box
                                              display="flex"
                                              alignItems="center"
                                              justifyContent="space-between"
                                              color="#6fd943"
                                              mb={0.5}
                                            >
                                              <Typography
                                                variant="subtitle1"
                                                fontWeight="bold"
                                                sx={{ fontSize: 15 }}
                                              >
                                                {ticket.title}
                                              </Typography>
                                              <Box
                                                sx={{
                                                  px: 1,
                                                  py: 0.2,
                                                  borderRadius: 1.5,
                                                  fontSize: 11,
                                                  fontWeight: "bold",
                                                  ...priorityBadgeStyles[
                                                    ticket.priority
                                                  ],
                                                  minWidth: 40,
                                                  textAlign: "center",
                                                  boxShadow:
                                                    "0 1px 2px rgba(0,0,0,0.13)",
                                                }}
                                              >
                                                {priorityLabels[
                                                  ticket.priority
                                                ] || ticket.priority}
                                              </Box>
                                            </Box>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                color: "#e0e0e0",
                                                fontWeight: 500,
                                                mb: 0.2,
                                                fontSize: 13,
                                              }}
                                            >
                                              الوصف: {ticket.description}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                color: "#e0e0e0",
                                                fontWeight: 500,
                                                mb: 0.2,
                                                fontSize: 13,
                                              }}
                                            >
                                              الاسم: {ticket.username}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "#b0b0b0",
                                                fontWeight: 400,
                                                mb: 0.2,
                                                fontSize: 12,
                                              }}
                                            >
                                              الشركة: {ticket.company}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "#b0b0b0",
                                                fontWeight: 400,
                                                mb: 0.2,
                                                fontSize: 12,
                                                marginRight: 5,
                                              }}
                                            >
                                              القسم: {ticket.department}
                                            </Typography>
                                            <Typography
                                              variant="caption"
                                              color="#a0a0a0"
                                              sx={{
                                                display: "block",
                                                mt: 0.5,
                                                fontSize: 11,
                                              }}
                                            >
                                              {ticket.created_at}
                                            </Typography>

                                            {/* Attachment Display */}
                                            {ticket.attachment && (
                                              <Box
                                                sx={{
                                                  mt: 0.5,
                                                  p: 0.5,
                                                  bgcolor:
                                                    "rgba(111,217,67,0.07)",
                                                  borderRadius: 1,
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  color="#6fd943"
                                                  sx={{
                                                    fontWeight: 600,
                                                    display: "block",
                                                    mb: 0.2,
                                                    fontSize: 11,
                                                  }}
                                                >
                                                  المرفق:
                                                </Typography>
                                                {ticket.attachment.match(
                                                  /\.(jpg|jpeg|png|gif)$/i
                                                ) ? (
                                                  <Box
                                                    sx={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 1,
                                                      justifyContent:
                                                        "space-between",
                                                    }}
                                                  >
                                                    <img
                                                      src={`https://backenderpforseg.samielmetwali.com/${ticket.attachment}`}
                                                      alt="attachment"
                                                      style={{
                                                        maxWidth: 60,
                                                        maxHeight: 60,
                                                        borderRadius: 4,
                                                        cursor: "pointer",
                                                        border:
                                                          "1px solid rgba(111,217,67,0.18)",
                                                      }}
                                                      onClick={() =>
                                                        window.open(
                                                          `https://backenderpforseg.samielmetwali.com/${ticket.attachment}`,
                                                          "_blank"
                                                        )
                                                      }
                                                    />
                                                    <Button
                                                      size="small"
                                                      startIcon={
                                                        <DownloadIcon />
                                                      }
                                                      sx={{
                                                        color: "#6fd943",
                                                        border:
                                                          "1px solid #6fd943",
                                                        fontSize: 11,
                                                        minWidth: 0,
                                                        px: 1.5,
                                                        py: 0.2,
                                                        "&:hover": {
                                                          bgcolor:
                                                            "rgba(111,217,67,0.1)",
                                                        },
                                                      }}
                                                      onClick={() => {
                                                        const link =
                                                          document.createElement(
                                                            "a"
                                                          );
                                                        link.href = `https://backenderpforseg.samielmetwali.com/${ticket.attachment}`;
                                                        link.download =
                                                          ticket.attachment
                                                            .split("/")
                                                            .pop();
                                                        link.click();
                                                      }}
                                                    >
                                                      تحميل
                                                    </Button>
                                                  </Box>
                                                ) : (
                                                  <Button
                                                    size="small"
                                                    startIcon={<DownloadIcon />}
                                                    sx={{
                                                      color: "#6fd943",
                                                      border:
                                                        "1px solid #6fd943",
                                                      fontSize: 11,
                                                      minWidth: 0,
                                                      px: 1.5,
                                                      py: 0.2,
                                                      "&:hover": {
                                                        bgcolor:
                                                          "rgba(111,217,67,0.1)",
                                                      },
                                                    }}
                                                    onClick={() => {
                                                      const link =
                                                        document.createElement(
                                                          "a"
                                                        );
                                                      link.href = `https://backenderpforseg.samielmetwali.com/${ticket.attachment}`;
                                                      link.download =
                                                        ticket.attachment
                                                          .split("/")
                                                          .pop();
                                                      link.click();
                                                    }}
                                                  >
                                                    تحميل الملف
                                                  </Button>
                                                )}
                                              </Box>
                                            )}
                                            <Box
                                              mt={1}
                                              display="flex"
                                              flexWrap="wrap"
                                              gap={0.5}
                                              justifyContent="space-between"
                                            >
                                              <Select
                                                labelId="assigned-label"
                                                value={
                                                  ticket.assigned_to ??
                                                  "غير مخصص"
                                                }
                                                onChange={(e) =>
                                                  handleAssign(
                                                    ticket.id,
                                                    e.target.value
                                                  )
                                                }
                                                displayEmpty
                                                size="small"
                                                sx={{
                                                  background: "#2d3134",
                                                  color: "#fff",
                                                  flexGrow: 1,
                                                  minWidth: 80,
                                                  fontSize: 12,
                                                  ".MuiSelect-select": {
                                                    py: 0.5,
                                                    px: 1,
                                                  },
                                                }}
                                              >
                                                <MenuItem
                                                  value="غير مخصص"
                                                  disabled
                                                >
                                                  غير مخصص
                                                </MenuItem>
                                                {employees.map((emp) => (
                                                  <MenuItem
                                                    key={emp.employee_id}
                                                    value={emp.employee_id}
                                                    sx={{ fontSize: 12 }}
                                                  >
                                                    {emp.name}
                                                  </MenuItem>
                                                ))}
                                              </Select>

                                              <Select
                                                size="small"
                                                value={
                                                  ticket.priority === "Low" ||
                                                  ticket.priority ===
                                                    "Normal" ||
                                                  ticket.priority === "High" ||
                                                  ticket.priority === "Critical"
                                                    ? ticket.priority
                                                    : "Normal"
                                                }
                                                onChange={(e) =>
                                                  handlePriorityChange(
                                                    ticket.id,
                                                    e.target.value
                                                  )
                                                }
                                                sx={{
                                                  backgroundColor: "#33373a",
                                                  color: "#fff",
                                                  borderRadius: 1,
                                                  flexGrow: 1,
                                                  minWidth: 70,
                                                  fontSize: 12,
                                                  ".MuiSelect-select": {
                                                    py: 0.5,
                                                    px: 1,
                                                  },
                                                  "& .MuiSvgIcon-root": {
                                                    color: "#6fd943",
                                                  },
                                                }}
                                              >
                                                <MenuItem value="Normal">
                                                  عادية
                                                </MenuItem>
                                                <MenuItem value="Low">
                                                  منخفضة
                                                </MenuItem>
                                                <MenuItem value="High">
                                                  مرتفعة
                                                </MenuItem>
                                                <MenuItem value="Critical">
                                                  حرجة
                                                </MenuItem>
                                              </Select>

                                              <IconButton
                                                size="small"
                                                onClick={() =>
                                                  confirmDelete(ticket.id)
                                                }
                                                sx={{
                                                  color: "#fff",
                                                  backgroundColor: "#d32f2f",
                                                  "&:hover": {
                                                    backgroundColor: "#b71c1c",
                                                  },
                                                  borderRadius: 1,
                                                  boxShadow:
                                                    "0 1px 4px 0 rgba(211,47,47,0.18)",
                                                  minWidth: 0,
                                                  height: 28,
                                                  width: 28,
                                                  p: 0.5,
                                                }}
                                                aria-label="حذف"
                                                title="حذف التذكرة"
                                              >
                                                <DeleteIcon fontSize="small" />
                                              </IconButton>

                                              <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                sx={{
                                                  ml: 0.5,
                                                  px: 1.5,
                                                  py: 0.2,
                                                  fontSize: 12,
                                                  minWidth: 0,
                                                }}
                                                onClick={() => {
                                                  setSelectedTicketId(
                                                    ticket.id
                                                  );
                                                  setAnswerText(
                                                    ticket.answer || ""
                                                  );
                                                  setOpenAnswerDialog(true);
                                                }}
                                              >
                                                إجابة
                                              </Button>
                                            </Box>
                                          </CardContent>
                                        </AnimatedCard>
                                      </div>
                                    </Grow>
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                          {provided.placeholder}
                        </ColumnContainer>
                      )}
                    </Droppable>
                  </Grid>
                ))}
              </Grid>
            </DragDropContext>
          </Box>
        )}
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={() => setOpenDeleteDialog(false)}
          TransitionComponent={Slide}
          transitionDuration={300}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              background: "linear-gradient(135deg, #232526 0%, #2d3134 100%)",
              color: "white",
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              border: "1px solid rgba(111, 217, 67, 0.2)",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: "#6fd943",
              fontWeight: 700,
              borderBottom: "1px solid rgba(255,255,255,0.1)",
            }}
          >
            تأكيد الحذف
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ mt: 2, color: "#e0e0e0" }}>
              هل أنت متأكد من حذف هذه التذكرة؟ لا يمكن التراجع عن هذا الإجراء.
            </Typography>
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: "1px solid rgba(255,255,255,0.1)",
              padding: "16px 24px",
            }}
          >
            <Button
              onClick={() => setOpenDeleteDialog(false)}
              sx={{
                color: "#6fd943",
                fontWeight: 600,
                "&:hover": {
                  backgroundColor: "rgba(111, 217, 67, 0.1)",
                },
              }}
            >
              إلغاء
            </Button>
            <Button
              onClick={deleteConfirmed}
              color="error"
              variant="contained"
              sx={{
                fontWeight: 600,
                background: "linear-gradient(135deg, #d32f2f 0%, #b71c1c 100%)",
                boxShadow: "0 4px 8px rgba(211,47,47,0.3)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #b71c1c 0%, #9a0007 100%)",
                  boxShadow: "0 6px 12px rgba(211,47,47,0.4)",
                },
              }}
            >
              حذف التذكرة
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={openAnswerDialog}
          onClose={() => setOpenAnswerDialog(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 2,
              background: "linear-gradient(135deg, #232526 0%, #2d3134 100%)",
              color: "#fff",
              boxShadow: "0 8px 32px rgba(111,217,67,0.15)",
              minWidth: 400,
            },
          }}
        >
          <DialogTitle
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              color: "#6fd943",
              fontWeight: 700,
              fontSize: "1.3rem",
              borderBottom: "1px solid rgba(255,255,255,0.08)",
              mb: 1,
            }}
          >
            <span style={{ fontSize: 24, marginLeft: 8 }}></span> إجابة التذكرة
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <TextField
              label="الإجابة"
              multiline
              minRows={3}
              fullWidth
              value={answerText}
              onChange={(e) => setAnswerText(e.target.value)}
              sx={{
                mt: 1,
                background: "#232526",
                borderRadius: 2,
                "& .MuiInputBase-root": {
                  color: "#fff",
                  fontFamily: "Cairo, sans-serif",
                },
                "& .MuiInputLabel-root": {
                  color: "#6fd943",
                  fontFamily: "Cairo, sans-serif",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6fd943",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#8ffb6f",
                },
              }}
              InputLabelProps={{
                style: { color: "#6fd943", fontFamily: "Cairo, sans-serif" },
              }}
              inputProps={{
                style: { color: "#fff", fontFamily: "Cairo, sans-serif" },
              }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              borderTop: "1px solid rgba(255,255,255,0.08)",
              pt: 2,
              pb: 1,
              px: 3,
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              onClick={() => setOpenAnswerDialog(false)}
              sx={{
                color: "#fff",
                background: "rgba(255,255,255,0.08)",
                borderRadius: 2,
                fontWeight: 600,
                px: 3,
                "&:hover": { background: "rgba(255,255,255,0.18)" },
              }}
            >
              إلغاء
            </Button>
            <Button
              variant="contained"
              color="success"
              sx={{
                background: "linear-gradient(90deg, #6fd943 0%, #4caf50 100%)",
                color: "#fff",
                borderRadius: 2,
                fontWeight: 700,
                px: 4,
                boxShadow: "0 2px 8px 0 rgba(111,217,67,0.15)",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #4caf50 0%, #6fd943 100%)",
                },
              }}
              onClick={async () => {
                await axios.post(
                  "https://backenderpforseg.samielmetwali.com/tickets/updateTicket.php",
                  {
                    id: selectedTicketId,
                    answer: answerText,
                  }
                );
                setOpenAnswerDialog(false);
                // يمكنك تحديث التذاكر هنا إذا أردت
              }}
            >
              حفظ الإجابة
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={deleteSuccess}
          onClose={() => setDeleteSuccess(false)}
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 2,
              backgroundColor: "#fefefe",
              boxShadow: 6,
              minWidth: 320,
            },
          }}
        >
          <DialogTitle
            sx={{ fontWeight: "bold", color: "#d32f2f", fontSize: "1.2rem" }}
          >
            تم الحذف بنجاح
          </DialogTitle>

          <DialogContent sx={{ color: "#333", fontSize: "1rem", mt: 1 }}>
            التذكرة تم حذفها نهائيًا من النظام.
          </DialogContent>

          <DialogActions sx={{ justifyContent: "flex-end", pt: 2 }}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setDeleteSuccess(false)}
              sx={{ borderRadius: 2, textTransform: "none" }}
              autoFocus
            >
              إغلاق
            </Button>
          </DialogActions>
        </Dialog>
        <AddTicketDialog
          open={openAddDialog}
          onClose={() => setOpenAddDialog(false)}
          onTicketAdded={(ticket) => setTickets((prev) => [ticket, ...prev])}
          employees={employees}
          companies={companies}
          user={user}
        />
      </Paper>
    </Fade>
  );
};

export default TicketsForAdmin;
